import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, CardHeader, CardContent, Typography, Paper, IconButton, makeStyles } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import emma_w from './../../../../global/imagenes/emma_w.png';
import TareasTerminadas from '../../../general/TareasTerminadas';
import sesionesBienestar from '../../../../global/imagenes/fases.png';
import bienestarFormato from '../../../../global/imagenes/formatosBienestar.png';
import { obtenerPermisosValorAlt } from '../../../../global/js/funciones';
import { MODULOS_FASES_BIENESTAR, COD_CRISIS, COD_CULTURA_C } from '../../../bienestar_estudiantil/helper';

const useStyles = makeStyles((theme) => ({
    card: {
        width: '100%', minHeight: 170, cursor: 'pointer', borderRadius: 8,
        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        '&:hover': { transform: 'translateY(-3px)' }
    },
    cardCompacto: {
        width: '100%', minHeight: 150, cursor: 'pointer', borderRadius: 8,
        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.08)',
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        '&:hover': { transform: 'translateY(-3px)' }
    },
    container: { 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 12, 
        justifyItems: 'stretch',
        width: '100%',
        padding: '0 8px',
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
        },
    },
    containerCompacto: { 
        display: 'grid', 
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 10, 
        justifyItems: 'stretch',
        width: '100%',
        padding: '0 8px',
        [theme.breakpoints.down('md')]: {
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        },
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
        },
    },
    cardHeader: { fontSize: '0.8rem', fontWeight: 700, textAlign: 'center', color: '#111', padding: '8px 8px 4px', lineHeight: 1.25, minHeight: 52 },
    cardHeaderCompacto: { fontSize: '0.75rem', fontWeight: 700, textAlign: 'center', color: '#111', padding: '6px 6px 3px', lineHeight: 1.2, minHeight: 48 },
    cardImage: { width: 56, height: 56, objectFit: 'cover', display: 'block', margin: '0 auto' },
    cardImageCompacto: { width: 50, height: 50, objectFit: 'cover', display: 'block', margin: '0 auto' },
}));

// Función para extraer el código de la fase (similar a extraerSesionNumero en FormatoSesiones)
const extraerCodigoFase = (fase) => {
    if (!fase) return null;
    return fase.valora || fase.valorc || fase.valor || null;
};

const normalizarCodigoModulo = (valor) => {
    const raw = String(valor || '').trim().toUpperCase();
    if (!raw) return null;

    const limpio = raw.replace(/\s+/g, '');
    const matchFase = limpio.match(/^F(\d+)[_\-.]?(\d{1,3})$/);
    if (matchFase) {
        return `F${matchFase[1]}_${String(matchFase[2]).padStart(3, '0')}`;
    }

    const matchTransversal = limpio.match(/^T[_\-.]?(\d+)$/);
    if (matchTransversal) {
        return `T_${matchTransversal[1]}`;
    }

    return limpio;
};

const FormatoFases = ({ 
    fases = [], onModuleSelect, loading = false, onFaseOpen, 
    formulariosCompletados = {}, modoLectura = false, modoLecturaFases = false, 
    sesionInicial = null, tipoServicio = null 
}) => {
    const classes = useStyles();
    const [selectedFase, setSelectedFase] = useState(null);
    const [loadingFase, setLoadingFase] = useState(false);
    const [minLoader, setMinLoader] = useState(true);
    const [modulosPermitidos, setModulosPermitidos] = useState([]);
    const [cargandoModulos, setCargandoModulos] = useState(false);

    const fasesOrdenadas = useMemo(() => {
        const getVal = x => parseFloat(x.valorc || x.valor || x.orden || x.id || 0);
        return [...fases].sort((a, b) => {
            const na = getVal(a), nb = getVal(b);
            return (!isNaN(na) && !isNaN(nb)) ? na - nb : String(a.nombre).localeCompare(String(b.nombre));
        });
    }, [fases]);

    const fasesFiltradas = useMemo(() => {
        const requiereFiltro = tipoServicio?.codigo === COD_CRISIS || tipoServicio === COD_CULTURA_C;
        return requiereFiltro ? fasesOrdenadas.filter(s => s.codigo === 'pri_se_bien') : fasesOrdenadas;
    }, [fasesOrdenadas, tipoServicio]);

    useEffect(() => {
        if (sesionInicial) setSelectedFase(sesionInicial);
    }, [sesionInicial]);

    useEffect(() => {
        setMinLoader(true);
        const t = setTimeout(() => setMinLoader(false), 500);
        return () => clearTimeout(t);
    }, [loading, fases.length, tipoServicio]);

    useEffect(() => {
        if (!selectedFase?.id) {
            setModulosPermitidos([]);
            return;
        }
        setCargandoModulos(true);
        obtenerPermisosValorAlt([
            { llave: 'generica', valor: MODULOS_FASES_BIENESTAR },
            { llave: 'principal', valor: selectedFase.id }
        ]).then(res => {
            setModulosPermitidos((res || []).map(m => ({ ...m, key: m.secundario?.codigo || m.id })));
        }).finally(() => setCargandoModulos(false));
    }, [selectedFase?.id]);

    const handleOpenFase = useCallback(async (s) => {
        setLoadingFase(true);
        if (typeof onFaseOpen === 'function') {
            try { await onFaseOpen(s); } catch (e) { /* silent */ }
        } else {
            await new Promise(r => setTimeout(r, 220));
        }
        setSelectedFase(s);
        setLoadingFase(false);
    }, [onFaseOpen]);

    const listoParaRender = !loading && fases.length > 0 && tipoServicio != null && !minLoader;
    if (listoParaRender) {
        if (selectedFase) {
            if (loadingFase || cargandoModulos) return <LoadingView mensaje="Cargando fase" />;
            
            const tieneMusModulos = modulosPermitidos.length > 6;
            const containerClass = tieneMusModulos ? classes.containerCompacto : classes.container;
            
            return (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', padding: '6px 0', background: '#fff', position: 'sticky', top: 0, zIndex: 10 }}>
                        <IconButton onClick={() => setSelectedFase(null)} size="small"><ArrowBackIcon /></IconButton>
                        <Typography variant='subtitle2' style={{ marginLeft: 4, fontSize: '0.95rem', fontWeight: 600 }}>{selectedFase.nombre || selectedFase.valor}</Typography>
                    </div>
                    <div className={containerClass} style={{ overflow: 'auto', flex: 1 }}>
                        {modulosPermitidos.map(m => (
                            <ModuloCard 
                                key={m.key} 
                                modulo={m} 
                                fase={selectedFase} 
                                clases={classes}
                                completados={formulariosCompletados}
                                modoLectura={modoLectura}
                                modoLecturaFases={modoLecturaFases}
                                onSelect={onModuleSelect}
                                compacto={tieneMusModulos}
                            />
                        ))}
                    </div>
                </div>
            );
        }

        return (
            <div className={classes.container} style={{ width: '100%', boxSizing: 'border-box' }}>
                {fasesFiltradas.map((s, i) => (
                    <Card key={s.id || i} className={classes.card} elevation={2} onClick={() => handleOpenFase(s)}>
                        <CardHeader title={s.nombre || s.valor || `Sesión ${i + 1}`} titleTypographyProps={{ className: classes.cardHeader }} />
                        <CardContent style={{ textAlign: 'center' }}>
                            <img src={s.archivo || s.imagen || sesionesBienestar} alt="" className={classes.cardImage} />
                            {s.descripcion && <Typography variant="body2" style={{ fontSize: '0.78rem', marginTop: 8 }}>{s.descripcion}</Typography>}
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    return <LoadingView mensaje="Cargando fases" />;
};

// Componentes internos para limpieza
const LoadingView = ({ mensaje }) => (
    <div style={{ padding: 24, display: 'flex', justifyContent: 'center', minHeight: 160 }}>
        <TareasTerminadas mensaje={mensaje} imagen={emma_w} widthImg="7%" cargando />
    </div>
);

const ModuloCard = ({ modulo, fase, clases, completados, modoLectura, modoLecturaFases, onSelect, compacto = false }) => {
    // Extraer código de fase y código de módulo
    const codigoFase = extraerCodigoFase(fase);
    const formulariosDeEstaFase = (codigoFase && completados[codigoFase]) || {};
    const moduloKey = normalizarCodigoModulo(modulo.key);
    const keysCompletados = Object.keys(formulariosDeEstaFase || {});
    const isCompleted = keysCompletados.some((key) => normalizarCodigoModulo(key) === moduloKey);
    const isDisabled = !modoLecturaFases && !modoLectura && isCompleted;

    const handleClick = () => {
        if (isDisabled) return;
        if (onSelect) onSelect(modulo.key, { modulo, fase });
    };

    return (
            <Card
            className={compacto ? clases.cardCompacto : clases.card}
            elevation={2}
            onClick={handleClick}
            style={{
                cursor: isDisabled ? 'default' : 'pointer',
                opacity: isDisabled ? 0.55 : 1,
                pointerEvents: isDisabled ? 'none' : 'auto',
                filter: isDisabled ? 'grayscale(35%)' : 'none'
            }}
        >
            <CardHeader title={modulo.secundario?.nombre || ''} titleTypographyProps={{ className: compacto ? clases.cardHeaderCompacto : clases.cardHeader }} />
            <CardContent style={{ textAlign: 'center', position: 'relative', padding: '10px 12px 12px' }}>
                <img src={bienestarFormato} alt="" className={compacto ? clases.cardImageCompacto : clases.cardImage} />
                {isCompleted && (
                    <CheckCircleIcon style={{ position: 'absolute', top: 0, right: 10, color: '#6cf871', fontSize: compacto ? 22 : 28 }} />
                )}
            </CardContent>
        </Card>
    );
};

export default FormatoFases;